/**
 * ProcurementGasCardsPanel — Procurement admin issues one gas card per
 * eligible field staff (Regional Manager / Supervisor / Crew Leader).
 * Deactivating a card is one-tap. Assigning a new card auto-deactivates
 * the previous one for the same staff member.
 */
import { useEffect, useState } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { CreditCard, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ProcurementGasCardsPanel() {
  const [cards, setCards] = useState([]);
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ user_id: '', card_number: '', provider: 'Fleet Card' });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const r = await fetch(`${API}/api/procurement/vehicles/gas-cards`, { credentials: 'include' });
    if (r.ok) setCards(await r.json());
    const s = await fetch(`${API}/api/vehicles/eligible-employees?fleet_kind=staff`, { credentials: 'include' });
    if (s.ok) {
      const list = await s.json();
      setStaff((Array.isArray(list) ? list : []).filter((u) => !(u.position || '').toLowerCase().includes('site agent')));
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.user_id || !form.card_number) return toast.error('Pick staff + card number');
    setBusy(true);
    const r = await fetch(`${API}/api/procurement/vehicles/gas-cards`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    });
    setBusy(false);
    if (r.ok) { toast.success('Card issued'); setForm({ user_id: '', card_number: '', provider: 'Fleet Card' }); load(); }
    else { const j = await r.json().catch(() => ({})); toast.error(j.detail || 'Failed'); }
  };

  const deactivate = async (id) => {
    const r = await fetch(`${API}/api/procurement/vehicles/gas-cards/${id}/deactivate`, {
      method: 'POST', credentials: 'include',
    });
    if (r.ok) { toast.success('Card deactivated'); load(); }
  };

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-5 mt-4" data-testid="procurement-gas-cards-panel">
      <div className="flex items-center gap-3 mb-3">
        <CreditCard className="h-5 w-5" style={{ color: '#22c55e' }} />
        <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Gas Cards
        </h3>
      </div>
      <p className="text-xs text-zinc-400 mb-3">One card per eligible staff. Assigning a new card deactivates the previous one.</p>

      <div className="grid sm:grid-cols-4 gap-3 items-end mb-4">
        <div className="sm:col-span-2">
          <Label className="text-xs text-zinc-400">Staff member</Label>
          <Select value={form.user_id} onValueChange={(v) => setForm((f) => ({ ...f, user_id: v }))}>
            <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="gc-staff-pick"><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              {staff.map((s) => <SelectItem key={s.user_id} value={s.user_id}>{s.name} · {s.position || s.role}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Card number</Label>
          <Input value={form.card_number} onChange={(e) => setForm((f) => ({ ...f, card_number: e.target.value }))} placeholder="4111 2222 3333 4567" className="bg-zinc-800 border-zinc-700 text-white font-mono" data-testid="gc-number" />
        </div>
        <div>
          <Label className="text-xs text-zinc-400">Provider</Label>
          <Input value={form.provider} onChange={(e) => setForm((f) => ({ ...f, provider: e.target.value }))} className="bg-zinc-800 border-zinc-700 text-white" data-testid="gc-provider" />
        </div>
        <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-500 text-white font-bold sm:col-span-1" data-testid="gc-submit">
          <Plus className="h-4 w-4 mr-2" /> Issue
        </Button>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase text-zinc-500 border-b border-zinc-800">
            <th className="text-left py-2 pr-3">Staff</th>
            <th className="text-left py-2 pr-3">Position</th>
            <th className="text-left py-2 pr-3">Card</th>
            <th className="text-left py-2 pr-3">Provider</th>
            <th className="text-left py-2 pr-3">Status</th>
            <th className="text-right py-2 pl-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {cards.map((c) => (
            <tr key={c.card_id} className="border-b border-zinc-800/60 text-zinc-200">
              <td className="py-3 pr-3">{c.user_name}</td>
              <td className="py-3 pr-3 text-zinc-500">{c.position}</td>
              <td className="py-3 pr-3 font-mono text-emerald-400" data-testid={`gc-row-${c.card_id}`}>{c.masked}</td>
              <td className="py-3 pr-3">{c.provider}</td>
              <td className="py-3 pr-3">
                {c.active ? <Badge className="bg-emerald-700 text-white">Active</Badge> : <Badge className="bg-zinc-700 text-white">Inactive</Badge>}
              </td>
              <td className="py-3 pl-3 text-right">
                {c.active && <Button size="sm" variant="secondary" onClick={() => deactivate(c.card_id)}>Deactivate</Button>}
              </td>
            </tr>
          ))}
          {!cards.length && <tr><td colSpan={6} className="text-center text-zinc-500 py-4">No cards issued yet.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}
